import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as UndoIcon } from '@assets/images/RichTextEditor/undo.svg'
import { ReactComponent as RedoIcon } from '@assets/images/RichTextEditor/redo.svg'
import { ReactComponent as FontSizeArrowDownIcon } from '@assets/images/RichTextEditor/font-size-arrow-down.svg'
import type {
  RichTextEditorAiActionId,
  RichTextEditorAiResultActionId,
  RichTextEditorAiToneId,
  RichTextEditorAttachment,
  RichTextEditorBodyActionId,
  RichTextEditorColorOption,
  RichTextEditorFontSize,
  RichTextEditorFooterActionId,
  RichTextEditorProps,
  RichTextEditorToolbarGroup,
  RichTextEditorToolbarItem,
  RichTextEditorToolbarItemId,
  RichTextEditorVariable,
  RichTextEditorVariant,
} from './RichTextEditor.types'
import {
  DEFAULT_BODY_ACTIONS,
  DEFAULT_EMAIL_FAX_TOOLBAR,
  DEFAULT_FOOTER_ACTIONS,
  DEFAULT_SMS_NOTES_TOOLBAR,
  DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES,
  RICH_TEXT_EDITOR_BG_COLOR_OPTIONS,
  RICH_TEXT_EDITOR_BODY_ACTION_LABELS,
  RICH_TEXT_EDITOR_FONT_SIZE_OPTIONS,
  RICH_TEXT_EDITOR_FOOTER_ACTION_LABELS,
  RICH_TEXT_EDITOR_TEXT_COLOR_OPTIONS,
  RICH_TEXT_EDITOR_TOOLBAR_ITEM_LABELS,
} from './RichTextEditor.types'
import RichTextEditorAiMenu from './RichTextEditorAiMenu.component'
import EmojiPicker, {
  Theme as EmojiPickerTheme,
  type EmojiClickData,
} from 'emoji-picker-react'
import {
  ALIGNMENT_ICONS,
  ALIGNMENT_OPTIONS,
  richTextEditorHasContent,
  useRichTextEditor,
} from './useRichTextEditor'
import { ReactComponent as BoldIcon } from '@assets/images/Icons/Bold.svg'
import { ReactComponent as ItalicIcon } from '@assets/images/Icons/Italic.svg'
import { ReactComponent as UnderlineIcon } from '@assets/images/Icons/Underline.svg'
import { ReactComponent as StrikethroughIcon } from '@assets/images/Icons/Strikethrough.svg'
import { ReactComponent as ListIcon } from '@assets/images/Icons/List.svg'
import { ReactComponent as EnumerateIcon } from '@assets/images/Icons/Enumerate.svg'
import { ReactComponent as BlockquoteIcon } from '@assets/images/Icons/Blockquote.svg'
import { ReactComponent as IndentLeftIcon } from '@assets/images/Icons/Indent_left.svg'
import { ReactComponent as IndentRightIcon } from '@assets/images/Icons/Indent_right.svg'
import { ReactComponent as RefineIcon } from '@assets/images/Icons/Refine.svg'
import { ReactComponent as WriteAiIcon } from '@assets/images/Icons/Write_ai.svg'
import { ReactComponent as AiWandIcon } from '@assets/images/Icons/Ai_wand.svg'
import { ReactComponent as VariableIcon } from '@assets/images/Icons/Variable.svg'
import { ReactComponent as LinkIcon } from '@assets/images/Icons/Link.svg'
import { ReactComponent as EmojiSmileIcon } from '@assets/images/Icons/Emoji_smile.svg'
import { ReactComponent as Add2Icon } from '@assets/images/Icons/Add_2.svg'
import { ReactComponent as SendFillIcon } from '@assets/images/Icons/Send.svg'
import { ReactComponent as TextColorIcon } from '@assets/images/Icons/Text_color.svg'
import { ReactComponent as BgColorIcon } from '@assets/images/Icons/Bg_color.svg'
import { ReactComponent as AttachmentIcon } from '@assets/images/Icons/Attachment.svg'
import { ReactComponent as RemoveIcon } from '@assets/images/Icons/multiply_outline.svg'
import { Tooltip } from '@components/ui'
import './RichTextEditor.css'

const DEFAULT_TOOLBAR_BY_VARIANT: Record<
  RichTextEditorVariant,
  RichTextEditorToolbarGroup[]
> = {
  'email-fax': DEFAULT_EMAIL_FAX_TOOLBAR,
  'sms-notes': DEFAULT_SMS_NOTES_TOOLBAR,
}

const formatFileSize = (size: number): string => {
  const mb = size / (1024 * 1024)

  if (mb >= 1) {
    return `${mb.toFixed(mb >= 10 ? 0 : 1)}MB`
  }

  const kb = size / 1024

  if (kb >= 1) {
    return `${Math.max(1, Math.round(kb))}KB`
  }

  return `${Math.max(1, size)}B`
}

const toAttachment = (file: File): RichTextEditorAttachment => ({
  id: `${file.name}-${file.size}-${file.lastModified}`,
  file,
  name: file.name,
  sizeLabel: formatFileSize(file.size),
})

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  variant = 'email-fax',
  value,
  defaultValue = '',
  placeholder = 'Type your message here or type "/" to enable AI functionality',
  disabled = false,
  darkMode = false,
  className = '',
  toolbarGroups,
  bodyActions,
  footerActions,
  fontSizeOptions = RICH_TEXT_EDITOR_FONT_SIZE_OPTIONS,
  onChange,
  variables,
  onVariableSelect,
  onSend,
  onToolbarAction,
  onBodyAction,
  onFooterAction,
  toolbarOnly = false,
  toolbarActions = false,
  onFontSizeChange,
  onAlignmentChange,
  textColorOptions = RICH_TEXT_EDITOR_TEXT_COLOR_OPTIONS,
  backgroundColorOptions = RICH_TEXT_EDITOR_BG_COLOR_OPTIONS,
  onTextColorChange,
  onBackgroundColorChange,
  onEmojiSelect,
  onAttach,
  onAttachmentsChange,
  attachAccept,
  attachMultiple = true,
  showAttachments = true,
  aiEnabled = false,
  aiResult = null,
  onAiAction,
  onAiToneSelect,
  onAiResultAction,
}) => {
  const [fontSize, setFontSize] = useState<RichTextEditorFontSize>('14 pt')
  const [alignmentMenuOpen, setAlignmentMenuOpen] = useState(false)
  const [fontSizeMenuOpen, setFontSizeMenuOpen] = useState(false)
  const [variableMenuOpen, setVariableMenuOpen] = useState(false)
  const [colorMenu, setColorMenu] = useState<'textColor' | 'bgColor' | null>(
    null,
  )
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
  const [attachments, setAttachments] = useState<RichTextEditorAttachment[]>([])
  const [aiMenuAnchor, setAiMenuAnchor] = useState<{
    top: number
    left: number
  } | null>(null)
  const alignmentMenuRef = useRef<HTMLDivElement>(null)
  const fontSizeMenuRef = useRef<HTMLDivElement>(null)
  const variableMenuRef = useRef<HTMLDivElement>(null)
  const colorMenuRef = useRef<HTMLDivElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const aiMenuRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const {
    editorRef,
    resolvedValue,
    activeCommands,
    alignment,
    setAlignment,
    applyAlignment,
    applyFontSize,
    applyTextColor,
    applyBackgroundColor,
    insertText,
    insertVariable,
    runToolbarAction,
    handleInput,
    handlePaste,
  } = useRichTextEditor({
    value,
    defaultValue,
    disabled,
    onChange,
  })

  const aiMenuOpen = aiMenuAnchor !== null

  const canSend = richTextEditorHasContent(resolvedValue)

  const hasVariables = Boolean(variables?.length)
  const defaultToolbarGroups =
    variant === 'sms-notes' && hasVariables
      ? DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES
      : DEFAULT_TOOLBAR_BY_VARIANT[variant]
  const resolvedToolbarGroups = toolbarGroups ?? defaultToolbarGroups
  const resolvedBodyActions = bodyActions ?? DEFAULT_BODY_ACTIONS[variant]
  const resolvedFooterActions = footerActions ?? DEFAULT_FOOTER_ACTIONS

  const rootClassName = useMemo(
    () =>
      [
        'ds-rich-text-editor',
        `ds-rich-text-editor--${variant}`,
        darkMode ? 'ds-rich-text-editor--dark' : '',
        disabled ? 'ds-rich-text-editor--disabled' : '',
        toolbarOnly ? 'ds-rich-text-editor--toolbar-only' : '',
        toolbarOnly && toolbarActions ? 'ds-rich-text-editor--toolbar-bar' : '',
        aiMenuOpen ? 'ds-rich-text-editor--ai-open' : '',
        emojiPickerOpen ? 'ds-rich-text-editor--emoji-open' : '',
        className,
      ]
        .filter(Boolean)
        .join(' '),
    [
      aiMenuOpen,
      className,
      darkMode,
      disabled,
      emojiPickerOpen,
      toolbarActions,
      toolbarOnly,
      variant,
    ],
  )

  /** Anchors the AI popover under the current selection, or under the body. */
  const openAiMenu = (): void => {
    const root = rootRef.current

    if (!root) {
      return
    }

    const rootRect = root.getBoundingClientRect()
    const editor = editorRef.current
    const selection = document.getSelection()

    if (
      selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      editor?.contains(selection.anchorNode)
    ) {
      const rect = selection.getRangeAt(0).getBoundingClientRect()

      setAiMenuAnchor({
        top: rect.bottom - rootRect.top + 6,
        left: rect.left - rootRect.left,
      })

      return
    }

    const editorRect = editor?.getBoundingClientRect()

    setAiMenuAnchor({
      top: editorRect ? editorRect.top - rootRect.top : 40,
      left: editorRect ? editorRect.left - rootRect.left : 8,
    })
  }

  const handleVariableSelect = (variable: RichTextEditorVariable): void => {
    setVariableMenuOpen(false)

    // In toolbar-only mode the host owns the content, so it does the inserting.
    if (!toolbarOnly) {
      insertVariable(variable)
    }
    onVariableSelect?.(variable)
  }

  const handleToolbarClick = (actionId: RichTextEditorToolbarItemId): void => {
    if (actionId === 'ai') {
      openAiMenu()
      onToolbarAction?.(actionId)
      return
    }

    if (!toolbarOnly) {
      runToolbarAction(actionId)
    }
    onToolbarAction?.(actionId)
  }

  const handleColorSelect = (
    kind: 'textColor' | 'bgColor',
    option: RichTextEditorColorOption,
  ): void => {
    if (!toolbarOnly) {
      if (kind === 'textColor') {
        applyTextColor(option.value)
      } else {
        applyBackgroundColor(option.value)
      }
    }

    if (kind === 'textColor') {
      onTextColorChange?.(option)
    } else {
      onBackgroundColorChange?.(option)
    }

    setColorMenu(null)
  }

  const handleAiAction = (actionId: RichTextEditorAiActionId): void => {
    onAiAction?.(actionId)
  }

  const handleAiToneSelect = (toneId: RichTextEditorAiToneId): void => {
    onAiToneSelect?.(toneId)
  }

  const handleAiResultAction = (
    actionId: RichTextEditorAiResultActionId,
    result: string,
  ): void => {
    onAiResultAction?.(actionId, result)

    // Regenerate and copy keep the popover up so the result can be reused.
    if (actionId !== 'regenerate' && actionId !== 'copy') {
      setAiMenuAnchor(null)
    }
  }

  const handleBodyActionClick = (
    actionId: RichTextEditorBodyActionId,
  ): void => {
    onBodyAction?.(actionId)
  }

  const handleFooterActionClick = (
    actionId: RichTextEditorFooterActionId,
  ): void => {
    if (actionId === 'send') {
      if (!canSend) {
        return
      }

      onSend?.()
    }

    if (actionId === 'magic') {
      openAiMenu()
    }

    onFooterAction?.(actionId)
  }

  const handleAttachInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const files = Array.from(event.target.files ?? [])

    // Reset so picking the same file twice in a row still fires a change.
    event.target.value = ''

    if (files.length === 0) {
      return
    }

    setAttachments((current) => {
      const seen = new Set(current.map((attachment) => attachment.id))
      const added = files
        .map(toAttachment)
        .filter((attachment) => !seen.has(attachment.id))
      const next = [...current, ...added]

      onAttachmentsChange?.(next.map((attachment) => attachment.file))

      return next
    })

    onAttach?.(files)
  }

  const handleAttachmentRemove = (id: string): void => {
    setAttachments((current) => {
      const next = current.filter((attachment) => attachment.id !== id)

      onAttachmentsChange?.(next.map((attachment) => attachment.file))

      return next
    })
  }

  const handleEmojiClick = (emojiData: EmojiClickData): void => {
    if (!toolbarOnly) {
      insertText(emojiData.emoji)
    }

    onEmojiSelect?.(emojiData.emoji)
    setEmojiPickerOpen(false)
  }

  useEffect(() => {
    if (
      !alignmentMenuOpen &&
      !fontSizeMenuOpen &&
      !variableMenuOpen &&
      !colorMenu &&
      !aiMenuOpen &&
      !emojiPickerOpen
    ) {
      return undefined
    }

    const handleOutsideClick = (event: MouseEvent): void => {
      const target = event.target as Node

      if (
        alignmentMenuRef.current &&
        !alignmentMenuRef.current.contains(target)
      ) {
        setAlignmentMenuOpen(false)
      }

      if (
        fontSizeMenuRef.current &&
        !fontSizeMenuRef.current.contains(target)
      ) {
        setFontSizeMenuOpen(false)
      }

      if (
        variableMenuRef.current &&
        !variableMenuRef.current.contains(target)
      ) {
        setVariableMenuOpen(false)
      }

      if (colorMenuRef.current && !colorMenuRef.current.contains(target)) {
        setColorMenu(null)
      }

      if (aiMenuRef.current && !aiMenuRef.current.contains(target)) {
        setAiMenuAnchor(null)
      }

      if (emojiPickerRef.current && !emojiPickerRef.current.contains(target)) {
        setEmojiPickerOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return
      }

      setAlignmentMenuOpen(false)
      setFontSizeMenuOpen(false)
      setVariableMenuOpen(false)
      setColorMenu(null)
      setAiMenuAnchor(null)
      setEmojiPickerOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [
    aiMenuOpen,
    alignmentMenuOpen,
    colorMenu,
    emojiPickerOpen,
    fontSizeMenuOpen,
    variableMenuOpen,
  ])

  useEffect(() => {
    if (!aiEnabled || disabled || toolbarOnly) {
      return undefined
    }

    const handleSelectionEnd = (): void => {
      const editor = editorRef.current
      const root = rootRef.current
      const selection = document.getSelection()

      if (!editor || !root || !selection || selection.rangeCount === 0) {
        return
      }

      if (selection.isCollapsed || !editor.contains(selection.anchorNode)) {
        return
      }

      const rect = selection.getRangeAt(0).getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()

      setAiMenuAnchor({
        top: rect.bottom - rootRect.top + 6,
        left: rect.left - rootRect.left,
      })
    }

    document.addEventListener('mouseup', handleSelectionEnd)
    document.addEventListener('keyup', handleSelectionEnd)

    return () => {
      document.removeEventListener('mouseup', handleSelectionEnd)
      document.removeEventListener('keyup', handleSelectionEnd)
    }
  }, [aiEnabled, disabled, editorRef, toolbarOnly])

  const renderToolbarButton = (
    actionId: RichTextEditorToolbarItemId,
  ): React.ReactNode => {
    const label = RICH_TEXT_EDITOR_TOOLBAR_ITEM_LABELS[actionId]
    const isActive = activeCommands.has(actionId)

    if (actionId === 'undo') {
      return (
        <Tooltip key={actionId} content={label} placement="bottom">
          <button
            type="button"
            className="ds-rich-text-editor__tool-btn"
            aria-label={label}
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleToolbarClick(actionId)}
          >
            <UndoIcon />
          </button>
        </Tooltip>
      )
    }

    if (actionId === 'redo') {
      return (
        <Tooltip key={actionId} content={label} placement="bottom">
          <button
            type="button"
            className="ds-rich-text-editor__tool-btn"
            aria-label={label}
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleToolbarClick(actionId)}
          >
            <RedoIcon />
          </button>
        </Tooltip>
      )
    }

    if (actionId === 'fontSize') {
      return (
        <div
          key={actionId}
          className="ds-rich-text-editor__font-size-wrap"
          ref={fontSizeMenuRef}
        >
          <Tooltip content={label} placement="bottom">
            <button
              type="button"
              className="ds-rich-text-editor__font-size-btn"
              aria-label={label}
              aria-haspopup="menu"
              aria-expanded={fontSizeMenuOpen}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setFontSizeMenuOpen((open) => !open)}
            >
              <span>{fontSize}</span>
              <FontSizeArrowDownIcon />
            </button>
          </Tooltip>
          {fontSizeMenuOpen ? (
            <div className="ds-rich-text-editor__font-size-menu" role="menu">
              {fontSizeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`ds-rich-text-editor__font-size-option${
                    fontSize === option
                      ? ' ds-rich-text-editor__font-size-option--active'
                      : ''
                  }`}
                  role="menuitemradio"
                  aria-checked={fontSize === option}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setFontSize(option)
                    if (toolbarOnly) {
                      onFontSizeChange?.(option)
                    } else {
                      applyFontSize(option)
                    }
                    setFontSizeMenuOpen(false)
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )
    }

    if (actionId === 'alignment') {
      const AlignmentIcon = ALIGNMENT_ICONS[alignment]

      return (
        <div
          key={actionId}
          className="ds-rich-text-editor__alignment"
          ref={alignmentMenuRef}
        >
          <Tooltip content={label} placement="bottom">
            <button
              type="button"
              className="ds-rich-text-editor__tool-btn ds-rich-text-editor__tool-btn--combo"
              aria-label={label}
              aria-haspopup="menu"
              aria-expanded={alignmentMenuOpen}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setAlignmentMenuOpen((open) => !open)}
            >
              <AlignmentIcon width={16} height={16} aria-hidden="true" />
              <i className="mgc mgc_down_small_fill" aria-hidden="true" />
            </button>
          </Tooltip>
          {alignmentMenuOpen ? (
            <div className="ds-rich-text-editor__alignment-menu" role="menu">
              {ALIGNMENT_OPTIONS.map((option) => {
                const OptionIcon = ALIGNMENT_ICONS[option]
                const optionLabel =
                  option.charAt(0).toUpperCase() + option.slice(1)
                return (
                  <Tooltip
                    key={option}
                    content={optionLabel}
                    placement="bottom"
                  >
                    <button
                      type="button"
                      className={`ds-rich-text-editor__alignment-option${
                        alignment === option
                          ? ' ds-rich-text-editor__alignment-option--active'
                          : ''
                      }`}
                      role="menuitemradio"
                      aria-checked={alignment === option}
                      aria-label={optionLabel}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        if (toolbarOnly) {
                          setAlignment(option)
                          onAlignmentChange?.(option)
                        } else {
                          applyAlignment(option)
                        }
                        setAlignmentMenuOpen(false)
                      }}
                    >
                      <OptionIcon
                        className="ds-rich-text-editor__alignment-icon"
                        width={16}
                        height={16}
                        aria-hidden="true"
                      />
                    </button>
                  </Tooltip>
                )
              })}
            </div>
          ) : null}
        </div>
      )
    }

    if (actionId === 'textColor' || actionId === 'bgColor') {
      const isOpen = colorMenu === actionId
      const ColorIcon = actionId === 'textColor' ? TextColorIcon : BgColorIcon
      const options =
        actionId === 'textColor' ? textColorOptions : backgroundColorOptions

      return (
        <div
          key={actionId}
          className="ds-rich-text-editor__color"
          ref={isOpen ? colorMenuRef : undefined}
        >
          <Tooltip content={label} placement="bottom">
            <button
              type="button"
              className="ds-rich-text-editor__tool-btn ds-rich-text-editor__tool-btn--combo"
              aria-label={label}
              aria-haspopup="menu"
              aria-expanded={isOpen}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setColorMenu(isOpen ? null : actionId)}
            >
              <ColorIcon width={16} height={16} aria-hidden="true" />
              <i className="mgc mgc_down_small_fill" aria-hidden="true" />
            </button>
          </Tooltip>
          {isOpen ? (
            <div className="ds-rich-text-editor__color-menu" role="menu">
              {options.map((option) => (
                <Tooltip
                  key={option.id}
                  content={option.label}
                  placement="bottom"
                >
                  <button
                    type="button"
                    className="ds-rich-text-editor__color-swatch"
                    role="menuitem"
                    aria-label={option.label}
                    style={{ background: option.value }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleColorSelect(actionId, option)}
                  />
                </Tooltip>
              ))}
            </div>
          ) : null}
        </div>
      )
    }

    if (actionId === 'variable' && hasVariables) {
      return (
        <div
          key={actionId}
          className="ds-rich-text-editor__variable-wrap"
          ref={variableMenuRef}
        >
          <Tooltip content={label} placement="bottom">
            <button
              type="button"
              className={`ds-rich-text-editor__tool-btn ds-rich-text-editor__tool-btn--x-variable${
                variableMenuOpen ? ' ds-rich-text-editor__tool-btn--active' : ''
              }`}
              aria-label={label}
              aria-haspopup="menu"
              aria-expanded={variableMenuOpen}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setVariableMenuOpen((open) => !open)
                onToolbarAction?.(actionId)
              }}
            >
              <VariableIcon width={16} height={16} aria-hidden="true" />
            </button>
          </Tooltip>
          {variableMenuOpen ? (
            <div className="ds-rich-text-editor__variable-menu" role="menu">
              {variables?.map((variable) => (
                <button
                  key={variable.id}
                  type="button"
                  className="ds-rich-text-editor__variable-option"
                  role="menuitem"
                  title={variable.label}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleVariableSelect(variable)}
                >
                  {variable.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )
    }

    const iconComponentMap: Partial<
      Record<
        RichTextEditorToolbarItemId,
        React.FunctionComponent<React.SVGProps<SVGSVGElement>>
      >
    > = {
      bold: BoldIcon,
      italic: ItalicIcon,
      underline: UnderlineIcon,
      strikethrough: StrikethroughIcon,
      bulletList: ListIcon,
      orderedList: EnumerateIcon,
      blockquote: BlockquoteIcon,
      indentLeft: IndentLeftIcon,
      indentRight: IndentRightIcon,
      highlight: RefineIcon,
      variable: VariableIcon,
      ai: WriteAiIcon,
      link: LinkIcon,
    }

    const IconComponent = iconComponentMap[actionId]
    const isVariable = actionId === 'variable'

    return (
      <Tooltip key={actionId} content={label} placement="bottom">
        <button
          type="button"
          className={`ds-rich-text-editor__tool-btn${
            isActive ? ' ds-rich-text-editor__tool-btn--active' : ''
          }${isVariable ? ' ds-rich-text-editor__tool-btn--x-variable' : ''}`}
          aria-label={label}
          aria-pressed={isActive || undefined}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => handleToolbarClick(actionId)}
        >
          {IconComponent ? (
            <IconComponent width={16} height={16} aria-hidden="true" />
          ) : null}
        </button>
      </Tooltip>
    )
  }

  const renderToolbarItem = (
    item: RichTextEditorToolbarItem,
    index: number,
  ): React.ReactNode => {
    if (item === 'divider') {
      return (
        <span
          key={`divider-${index}`}
          className="ds-rich-text-editor__toolbar-divider"
          aria-hidden="true"
        />
      )
    }

    if (Array.isArray(item)) {
      return item.map((nestedItem) => renderToolbarButton(nestedItem))
    }

    return renderToolbarButton(item)
  }

  const renderBodyAction = (
    actionId: RichTextEditorBodyActionId,
  ): React.ReactNode => {
    const label = RICH_TEXT_EDITOR_BODY_ACTION_LABELS[actionId]
    const hasMenu = actionId === 'template' || actionId === 'variables'

    return (
      <button
        key={actionId}
        type="button"
        className="ds-rich-text-editor__chip"
        disabled={disabled}
        onClick={() => handleBodyActionClick(actionId)}
      >
        {actionId === 'refine' ? (
          <WriteAiIcon
            className="ds-rich-text-editor__chip-icon"
            width={16}
            height={16}
            aria-hidden="true"
          />
        ) : null}
        {actionId === 'variables' ? (
          <VariableIcon
            className="ds-rich-text-editor__chip-icon"
            width={16}
            height={16}
            aria-hidden="true"
          />
        ) : null}
        {label}
        {hasMenu ? (
          <i className="mgc mgc_down_small_fill" aria-hidden="true" />
        ) : null}
      </button>
    )
  }

  const renderFooterAction = (
    actionId: RichTextEditorFooterActionId,
  ): React.ReactNode => {
    const label = RICH_TEXT_EDITOR_FOOTER_ACTION_LABELS[actionId]

    if (actionId === 'send') {
      return (
        <button
          key={actionId}
          type="button"
          className={`ds-rich-text-editor__send-btn${
            canSend ? ' ds-rich-text-editor__send-btn--ready' : ''
          }`}
          aria-label={label}
          aria-disabled={!canSend || disabled}
          disabled={disabled}
          onClick={() => handleFooterActionClick(actionId)}
        >
          <SendFillIcon width={14} height={14} aria-hidden="true" />
        </button>
      )
    }

    if (actionId === 'magic') {
      return (
        <button
          key={actionId}
          type="button"
          className={`ds-rich-text-editor__icon-btn ds-rich-text-editor__icon-btn--magic${
            aiMenuOpen ? ' ds-rich-text-editor__icon-btn--active' : ''
          }`}
          aria-label={label}
          aria-pressed={aiMenuOpen}
          aria-expanded={aiMenuOpen}
          disabled={disabled}
          onClick={() => handleFooterActionClick(actionId)}
        >
          <AiWandIcon width={16} height={16} aria-hidden="true" />
        </button>
      )
    }

    if (actionId === 'emoji') {
      return (
        <button
          key={actionId}
          type="button"
          className={`ds-rich-text-editor__icon-btn${
            emojiPickerOpen ? ' ds-rich-text-editor__icon-btn--active' : ''
          }`}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={emojiPickerOpen}
          disabled={disabled}
          // Keeps the caret in the editor so the emoji lands where it was.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setEmojiPickerOpen((open) => !open)
            onFooterAction?.(actionId)
          }}
        >
          <EmojiSmileIcon width={16} height={16} aria-hidden="true" />
        </button>
      )
    }

    return (
      <React.Fragment key={actionId}>
        <button
          type="button"
          className="ds-rich-text-editor__icon-btn"
          aria-label={label}
          disabled={disabled}
          onClick={() => {
            fileInputRef.current?.click()
            handleFooterActionClick(actionId)
          }}
        >
          <Add2Icon width={16} height={16} aria-hidden="true" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="ds-rich-text-editor__file-input"
          accept={attachAccept}
          multiple={attachMultiple}
          tabIndex={-1}
          aria-hidden
          onChange={handleAttachInputChange}
        />
      </React.Fragment>
    )
  }

  return (
    <div className={rootClassName} ref={rootRef}>
      <div className="ds-rich-text-editor__toolbar">
        <div className="ds-rich-text-editor__toolbar-main">
          {resolvedToolbarGroups.map((group, groupIndex) => (
            <div
              key={`toolbar-group-${groupIndex}`}
              className="ds-rich-text-editor__toolbar-group"
            >
              {group.map((item, itemIndex) =>
                renderToolbarItem(item, itemIndex),
              )}
            </div>
          ))}
        </div>

        {toolbarOnly && toolbarActions && resolvedFooterActions.length > 0 ? (
          <div className="ds-rich-text-editor__actions">
            {resolvedFooterActions.map(renderFooterAction)}
          </div>
        ) : null}
      </div>

      {toolbarOnly ? null : (
        <>
          <div className="ds-rich-text-editor__body">
            {resolvedBodyActions.length > 0 ? (
              <div className="ds-rich-text-editor__body-actions">
                {resolvedBodyActions.map(renderBodyAction)}
              </div>
            ) : null}
            <div
              ref={editorRef}
              className="ds-rich-text-editor__input"
              contentEditable={!disabled}
              role="textbox"
              aria-multiline="true"
              aria-label={placeholder}
              data-placeholder={placeholder}
              tabIndex={disabled ? -1 : 0}
              suppressContentEditableWarning
              onInput={handleInput}
              onPaste={handlePaste}
            />

            {showAttachments && attachments.length > 0 ? (
              <ul className="ds-rich-text-editor__attachments">
                {attachments.map((attachment) => (
                  <li
                    key={attachment.id}
                    className="ds-rich-text-editor__attachment"
                  >
                    <AttachmentIcon
                      className="ds-rich-text-editor__attachment-icon"
                      width={16}
                      height={16}
                      aria-hidden="true"
                    />
                    <span className="ds-rich-text-editor__attachment-meta">
                      <span className="ds-rich-text-editor__attachment-name">
                        {attachment.name}
                      </span>
                      <span className="ds-rich-text-editor__attachment-size">
                        {attachment.sizeLabel}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="ds-rich-text-editor__attachment-remove"
                      aria-label={`Remove ${attachment.name}`}
                      disabled={disabled}
                      onClick={() => handleAttachmentRemove(attachment.id)}
                    >
                      <RemoveIcon width={12} height={12} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {resolvedFooterActions.length > 0 ? (
            <div className="ds-rich-text-editor__footer">
              <div className="ds-rich-text-editor__actions">
                {resolvedFooterActions.map(renderFooterAction)}
              </div>
            </div>
          ) : null}
        </>
      )}

      {emojiPickerOpen ? (
        <div
          ref={emojiPickerRef}
          className="ds-rich-text-editor__emoji-popover"
          role="dialog"
          aria-label="Pick an emoji"
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={darkMode ? EmojiPickerTheme.DARK : EmojiPickerTheme.LIGHT}
            width={320}
            height={380}
            previewConfig={{ showPreview: false }}
            lazyLoadEmojis
          />
        </div>
      ) : null}

      {aiMenuAnchor ? (
        <RichTextEditorAiMenu
          ref={aiMenuRef}
          result={aiResult}
          style={{ top: aiMenuAnchor.top, left: aiMenuAnchor.left }}
          onAction={handleAiAction}
          onToneSelect={handleAiToneSelect}
          onResultAction={handleAiResultAction}
        />
      ) : null}
    </div>
  )
}

export default RichTextEditor
